import { Box, Button, Grid, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FlexBetween from "../../components/FlexBetween";
import { addEditExecutionSubGroupSchema } from "../../schemas";
import useFetchExecutionSubGroupById from "../../hooks/useFetchExecutionSubGroupById";
import {
  addExecutionSubGroup,
  updateExecutionSubGroup,
} from "../../services/executionSubGroup";
import Loader from "../../components/Molecules/Loader/Loader";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useToast } from "../../services/utils";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";
import { useAuth } from "../../contexts/AuthContext";

interface IExecutionSubGroupForm {
  code: string | null;
  name: string | null;
  description: string | null;
}

const AddEditExecutionSubGroup: React.FC = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isAdmin } = useAuth();
  const { id: executionSubGroupId } = useParams();
  const [formData, setFormData] = useState<IExecutionSubGroupForm>({
    code: "",
    name: "",
    description: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [initialFormData, setInitialFormData] = useState<any>({});
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] =
    useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);

  // hooks
  const [executionSubGroupData] = useFetchExecutionSubGroupById({
    setIsLoading,
    currentExecutionSubGroupId: executionSubGroupId,
  });
  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: executionSubGroupData?.name,
      id: executionSubGroupData?.id,
    });
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [executionSubGroupData]);

  const handleFormData = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    validateField(e.target.name, e.target.value);
  };

  const validateField = async (name: any, value: any) => {
    try {
      // Create a partial form data object with only the field being changed
      const partialFormData = { ...formData, [name]: value };
      await addEditExecutionSubGroupSchema.validateAt(name, partialFormData);
      // If validation passes, clear any previous errors for this field
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined as any }));
    } catch (validationError: any) {
      // If validation fails, set the error message for this field
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  const buildPayload = () => {
    // Mirrors onSaveExecutionSubGroup which spreads all formData fields,
    // including empty-string values, without any filtering.
    return { ...formData };
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const isEditMode = Boolean(executionSubGroupId);
      const validateReqBody = {
        target_endpoint: isEditMode
          ? `/execution-sub-group/${executionSubGroupId}`
          : "/execution-sub-group",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id: executionSubGroupId } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast("Payload is valid!", "success");
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    const payload = buildPayload();
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    showToast("Payload copied to clipboard!", "success");
  };

  // handle event when save
  const onSaveExecutionSubGroup = async (e: any) => {
    const serviceAction = executionSubGroupId
      ? updateExecutionSubGroup
      : addExecutionSubGroup;
    e.preventDefault();
    try {
      await addEditExecutionSubGroupSchema.validate(formData, {
        abortEarly: false,
      });
      const reqBody = {
        ...formData,
      };
      serviceAction({
        currentExecutionSubGroupId: executionSubGroupId,
        payload: reqBody,
      })
        .then((response) => {
          if (response) {
            showToast(
              `Execution Sub Group ${
                executionSubGroupId ? "updated" : "created"
              } successfully!`,
              "success"
            );
            navigate("/execution-sub-group");
          }
        })
        .catch((error) => {
          console.error(
            `Cannot ${
              executionSubGroupId ? "update" : "create"
            } execution sub group`
          );
        });
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach(
        (error: { path: string | number; message: string }) => {
          newErrors[error.path] = error.message;
        }
      );
      setErrors((prevErrors) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };

  useEffect(() => {
    if (executionSubGroupData?.id) {
      setFormData({
        ...executionSubGroupData,
      });
      setInitialFormData(JSON.parse(JSON.stringify(executionSubGroupData)));
    }
  }, [executionSubGroupData]);

  // Update hasChanges when formData or initialFormData changes
  useEffect(() => {
    const formChanged =
      JSON.stringify(formData) !== JSON.stringify(initialFormData);

    setHasChanges(formChanged);
  }, [formData, initialFormData]);

  return (
    <Box>
      <Loader isLoading={isLoading} />
      <form onSubmit={onSaveExecutionSubGroup} autoComplete="off">
        <Box className="text-box-card compact-text-box-card">
          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Name<span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="name"
                onChange={handleFormData}
                className={`form-control ${errors?.name ? "has-error" : ""}`}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                error={!!errors?.name}
                helperText={errors?.name || ""}
                value={formData?.name}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Code<span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="code"
                disabled={!!executionSubGroupId}
                className={`form-control ${errors?.code ? "has-error" : ""}`}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                error={!!errors?.code}
                helperText={errors?.code || ""}
                value={formData?.code}
                onChange={(e: any) => {
                  if (!executionSubGroupData?.code) {
                    const sanitizedValue = e.target.value.replace(/\s/g, "");
                    handleFormData({
                      ...e,
                      target: {
                        ...e.target,
                        name: e.target.name,
                        value: sanitizedValue,
                      },
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">Description</label>
              <TextField
                type="text"
                name="description"
                onChange={handleFormData}
                className="form-control"
                value={formData?.description}
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <FlexBetween
            gap="3rem"
            sx={{ marginTop: 4, display: "flex", justifyContent: "flex-end" }}
          >
            {isAdmin && (
              <ViewJsonSplitButton
                onViewJson={() => {
                  setValidationResponse(null);
                  setIsPayloadDialogOpen(true);
                }}
                onCopyJson={handleCopyPayload}
              />
            )}
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={executionSubGroupId ? !hasChanges : false}
            >
              <SaveOutlinedIcon /> &nbsp; Save
            </Button>
          </FlexBetween>
        </Box>
      </form>

      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle="Execution Sub Group JSON"
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </Box>
  );
};

export default AddEditExecutionSubGroup;
